import React, { useState } from 'react';
import {
	SdfButton,
	SdfAvatar,
	SdfTextarea,
	SdfAlert,
	SdfFocusPane,
	SdfBusyIndicator
} from '@synerg/react-components';
import axios from 'axios';
import { trim } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { descriptionLength } from '../../common';

export const ContactUs = ({
	position,
	rhetorical,
	hasIcon,
	name = '',
	id,
}) => {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [toastShown, setToastShown] = useState(false);
	const [error, setError] = useState(false);
	const [alertMsg, setAlertMsg] = useState(null);
	const [messageLength, setMessageLength] = useState(0);
	const [numberOfEndLineCharacter, setNumberOfEndLineCharacter] = useState(0);

	const { control, handleSubmit, reset } = useForm({
		message: '',
	});

	const openModal = (position) => {
		if (position === 'technical') {
			window.open(
				"https://partnersupport.adp.com/hc/en-us/requests/new",
				"_blank"
			);
		} else {
			setOpen(true);
		}
	};

	const closeModal = () => {
		setOpen(false);
		reset({message: ''});
		setToastShown(false);
		setNumberOfEndLineCharacter(0);
		setMessageLength(0);
	};

	const validate = {
		inValidCharacters: value => !(/[^A-Za-z0-9-., ]/.test(trim(value))) || 'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), spaces and comma (,)',
	};

	const updateMessageLength = (value) => {
		const { endLineChar, lengthWithoutEndlineChar } = descriptionLength(value);
		setNumberOfEndLineCharacter(endLineChar);
		setMessageLength(lengthWithoutEndlineChar);
	};

	const ShowError = ({ message }) => {
		return (
		<SdfAlert slot="alert" size="sm" status="error" type="inline">
			{message}
		</SdfAlert>
		);
	};

	const positionMessage = {
		business: "business development advisor",
		technical: "technical advisor",
		partner: "partner development advisor",
	}[position];
	const team = {
		business: "business development team",
		technical: "technical team",
		partner: "partner development team",
	}[position];

	let initials = "";
	const nameFirst = name.split(" ").map((s) => s[0]);

	if (nameFirst.length > 2) {
		initials = nameFirst.slice(0, 2).join("");
	} else {
		initials = nameFirst.slice(0, 1).join("");
	}

	// const initials = name.split(' ').map(s => s[0]).slice(0, 2);
	const image = hasIcon ? `/self-service/api/contacts/${id}/image` : null;

	async function sendMessage(formData) {
		if (trim(formData.message).length === 0) {
			setError(true);
			setToastShown(true);
			setAlertMsg('Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces');
			return;
		}
		setLoading(true);
		setError(false);
		setToastShown(false);
		try {
			await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/contact`, {
				id,
				...formData,
				position,
			});
			setToastShown(true);
			setLoading(false);
			setAlertMsg("Thank you, Your message is saved");
			closeModal();
		} catch (e) {
			setError(true);
			setAlertMsg("Something went wrong, Try again!");
			setLoading(false);
			setToastShown(true);
		}
	}

	return (
		<div>
			<SdfButton
				className="w-full"
				action="standard"
				emphasis={"secondary"}
				size="sm"
				variant={"contained"}
				onClick={() => openModal(position)}
			>
				{rhetorical}
			</SdfButton>

			<SdfFocusPane
				visible={open}
				hideDismissButton={true}
				hideAcceptButton={true}
				onSdfDismiss={() => {
					closeModal();
				}}
				closeable={true}
				dismissOnClickOutside={true}
				heading={rhetorical}
				spacing={"loose"}
				id="sdf-modal"
				size="md"
			>
				{loading ? (
				<SdfBusyIndicator
					style={{ color: "#1a8099" }}
					size="xl"
					fullScreen={true}
					overlay={true}
				/>
				) : null}

				<SdfAlert
					hidden={!toastShown}
					autoClose={true}
					autoCloseAfter={3000}
					closeable={true}
					useAnimation={true}
					status={error ? "error" : "success"}
				>
					{alertMsg}
				</SdfAlert>

				<div className="flex flex-row mt-6 default-text pr-1 pl-1">
					<div className="pr-4 pb-4">
						<SdfAvatar
							src={image}
							size={"xl"}
							shape={"circle"}
							initials={initials}
						/>
					</div>
					<div className="pb-4 pl-4">
						<div className="text-heading-03 mb-5">
							Send your{" "}
							<b>
								{name + ", " + positionMessage + ", "}
							</b> a message and a member of our {team} will reply to you as soon as
							possible.
						</div>
						<div className="">
							<Controller
								name="message"
								control={control}
								rules={{
									validate: validate.inValidCharacters
								}}
								render={({
									field: { value, onChange },
									fieldState: { error },
								}) => (
									<SdfTextarea
										name="message"
										label="Message"
										rows={6}
										onSdfInput={event => { onChange(event.detail); updateMessageLength(event.detail) }}
										id="sdf-message"
										value={value}
										maxlength={256 + numberOfEndLineCharacter}
										state={error ? 'error' : 'normal'}
										resize="none"
									>
										{error && error.message &&
											<ShowError message={error.message} />
										}
									</SdfTextarea>
								)}
							/>
							<div className='mt-2 text-right'>{messageLength}/256</div>
						</div>
					</div>
				</div>
				<SdfButton slot="custom-buttons" onClick={closeModal}>Cancel</SdfButton>
				<SdfButton
					slot="custom-buttons"
					emphasis="primary"
					disabled={messageLength >= 3 ? undefined : true}
					onClick={e => {
						handleSubmit(data => sendMessage(data))();
					}}
				>
					Save
				</SdfButton>
			</SdfFocusPane>
		</div>
	);
};
